import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import _ from 'lodash';

import AccountContext from 'contexts/AccountContext';
import BoardContext from 'contexts/BoardContext';
import isPerformanceBoard from 'isPerformanceBoard';
import PerformanceBoardSettingsContext from '../../contexts/PerformanceBoardSettingsContext';
import useUpdateGeneral from '../hooks/useUpdateGeneral';
import BoardDriverAppEmbedSettings from './BoardDriverAppEmbedSettings';

const normalizeEmbed = ({
  isButtonEnabled,
  carrierSlug,
  programSlug,
}: {
  isButtonEnabled: boolean;
  carrierSlug: string;
  programSlug: string;
}): NonNullable<PerformanceBoardTypes.Board['driverAppEmbed']> => {
  const trimmedCarrier = carrierSlug.trim();
  const trimmedProgram = programSlug.trim();
  return {
    isButtonEnabled,
    ...(trimmedCarrier ? { carrierSlug: trimmedCarrier } : {}),
    ...(trimmedProgram ? { programSlug: trimmedProgram } : {}),
  };
};

const BoardDriverAppEmbedSettingsContainer = ({
  board,
}: {
  board: PerformanceBoardTypes.Board;
}) => {
  const { driverAppEmbedCarrierSlug, driverAppEmbedProgramSlug } =
    useContext(AccountContext);
  const { setHasUnsavedChanges, hasUnsavedChanges } = useContext(
    PerformanceBoardSettingsContext,
  );
  const updateGeneral = useUpdateGeneral();

  const [isButtonEnabled, setIsButtonEnabled] = useState<boolean>(
    () => board.driverAppEmbed?.isButtonEnabled ?? false,
  );
  const [carrierSlug, setCarrierSlug] = useState<string>(
    () => board.driverAppEmbed?.carrierSlug ?? '',
  );
  const [programSlug, setProgramSlug] = useState<string>(
    () => board.driverAppEmbed?.programSlug ?? '',
  );

  const currentEmbed = useMemo(
    () =>
      normalizeEmbed({
        isButtonEnabled: board.driverAppEmbed?.isButtonEnabled ?? false,
        carrierSlug: board.driverAppEmbed?.carrierSlug ?? '',
        programSlug: board.driverAppEmbed?.programSlug ?? '',
      }),
    [board.driverAppEmbed],
  );

  const draftEmbed = useMemo(
    () => normalizeEmbed({ isButtonEnabled, carrierSlug, programSlug }),
    [isButtonEnabled, carrierSlug, programSlug],
  );

  useEffect(() => {
    setHasUnsavedChanges(!_.isEqual(draftEmbed, currentEmbed));
  }, [draftEmbed, currentEmbed, setHasUnsavedChanges]);

  const onCarrierSlugChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setCarrierSlug(event.target.value);
  };
  const onProgramSlugChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setProgramSlug(event.target.value);
  };

  const handleSave = useCallback(() => {
    const draftBoard: PerformanceBoardTypes.Board = {
      ...board,
      driverAppEmbed: draftEmbed,
    };
    updateGeneral({ draftBoard });
  }, [board, draftEmbed, updateGeneral]);

  return (
    <BoardDriverAppEmbedSettings
      isButtonEnabled={isButtonEnabled}
      setIsButtonEnabled={setIsButtonEnabled}
      carrierSlug={carrierSlug}
      onCarrierSlugChanged={onCarrierSlugChanged}
      programSlug={programSlug}
      onProgramSlugChanged={onProgramSlugChanged}
      accountCarrierSlug={driverAppEmbedCarrierSlug}
      accountProgramSlug={driverAppEmbedProgramSlug}
      hasUnsavedChanges={hasUnsavedChanges}
      handleSave={handleSave}
    />
  );
};

const Gate = () => {
  const { board } = useContext(BoardContext);
  if (isPerformanceBoard(board)) {
    return <BoardDriverAppEmbedSettingsContainer board={board} />;
  }
  return null;
};

export default Gate;
